import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { PARTNER_CATEGORIES, PARTNERS_VIDEO_URL } from './categories';
import { resolveCategorySlug } from './categorySlugs';
import { safeReturnTo } from './returnTo';

/**
 * Экран 2.8.1 — вход в Блок 8 «Партнёры» (каталог подрядчиков, ADR-0080).
 * UI по утверждённому прототипу `screen-2.8-partners.html` (ScreenCategories).
 *
 * Порядок контента: VideoButton первым (P-040) → заголовок «Партнёры» →
 * 11 категорий вертикально (P-019) → жёлтая кнопка «Напиши что нужно улучшить
 * подрядчику». Layout shell P-046, ScrollToTop глобальный (P-047), «Назад» =
 * история (P-036, в BottomBar/Header). Экран статический (без async) → состояния
 * loading/error не нужны.
 *
 * Дизайн-система: bg-canvas/bg-card/text-ink (P-038/P-039), Manrope (P-043),
 * палитра только через токены (P-045). Жёлтый (accent) — заливка кнопки отзыва,
 * НЕ цвет текста (P-041). Переключатель языка на экране нет (глобальный, P-020).
 *
 * **Приём из калькулятора (Фаза F2, ADR-0080 §8.1):** `/partners?category=<slug>`
 * (+опц. `&returnTo=<encoded>`). Валидный slug → резолв в каноничную(ые) категорию(и)
 * (`facades-radius` → 2) и редирект в `/partners/list` (repeated-param `?category=…`,
 * `replace` — чтобы «Назад» из списка не зацикливал резолв). Неизвестный/без slug →
 * обычный экран категорий (поведение preserved). `returnTo` (валидный, внутренний)
 * пробрасывается в список и питает «Назад» (R3); на самом экране категорий — тоже.
 */
export function PartnersCategories() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const returnTo = safeReturnTo(params.get('returnTo'));
  const resolved = resolveCategorySlug(params.get('category'));

  // Валидный slug → форвард в список с резолвнутыми категориями (repeated-param) +
  // returnTo. `replace` убирает `/partners?category=slug` из истории (анти-зацикливание).
  if (resolved) {
    const sp = new URLSearchParams();
    for (const c of resolved) sp.append('category', c);
    if (returnTo) sp.set('returnTo', returnTo);
    return <Navigate to={`/partners/list?${sp.toString()}`} replace />;
  }

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Партнёры" />

      <section className="flex-1 overflow-y-auto flex flex-col gap-3 p-6">
        {/* VideoButton первым контент-элементом (P-040). Заголовок «Партнёры» —
            в Header (h1), в контенте не дублируем (как FurnitureEntry; a11y). */}
        <VideoButton videoUrl={PARTNERS_VIDEO_URL} />

        {/* 11 категорий — вертикальный список (P-019), канон-имена (P-001) */}
        <div className="flex flex-col gap-2">
          {PARTNER_CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => navigate(`/partners/list?category=${encodeURIComponent(category)}`)}
              aria-label={category}
              className={
                'min-h-tap w-full rounded-xl bg-card border-[1.5px] border-accent ' +
                'text-ink font-body text-lg font-medium shadow-card ' +
                'flex items-center justify-between gap-3 px-4 py-3 text-left ' +
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90'
              }
            >
              <span className="flex-1">{category}</span>
              <span aria-hidden="true" className="text-ink opacity-40">
                ›
              </span>
            </button>
          ))}
        </div>

        {/* Жёлтая кнопка (accent fill, ink text — P-041 разрешает заливку) → форма отзыва (Фаза E) */}
        <button
          type="button"
          onClick={() => navigate('/partners/review')}
          aria-label="Напиши что нужно улучшить подрядчику"
          className={
            'min-h-tap w-full rounded-xl bg-accent text-ink font-body text-lg font-medium ' +
            'shadow-card px-4 py-3 mt-3 ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90'
          }
        >
          Напиши что нужно улучшить подрядчику
        </button>
      </section>

      {/* F2: при наличии валидного returnTo «Назад» ведёт в калькулятор-анкету (R3). */}
      <BottomBar backTo={returnTo ?? undefined} />
    </main>
  );
}
